import { FieldTrigger, type FieldTriggerProps } from '@/components/ui/field-trigger'
import { Calendar, type CalendarProps } from '@/components/ui/calendar'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  type PopoverContentProps,
} from '@/components/ui/popover'
import { useIsReadOnly } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { useFieldContext } from '@/lib/form-contexts'
import type { ConfigType } from 'dayjs'
import { ChevronDownIcon } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import type { Matcher } from 'react-day-picker'
import { Typography } from '@/components/ui/typography'
import FieldInfo from './FieldInfo'
import { toYmdFromDate, toCalendarDate, cn } from '@/lib/utils'

type DisabledMatcher = Matcher | Matcher[]

const buildRangeDisabledMatcher = (
  minDateYmd: string | null,
  maxDateYmd: string | null
): ((date: Date) => boolean) | undefined => {
  if (!minDateYmd && !maxDateYmd) {
    return undefined
  }

  return (date: Date) => {
    const dateYmd = toYmdFromDate(date)

    if (minDateYmd && dateYmd < minDateYmd) {
      return true
    }

    if (maxDateYmd && dateYmd > maxDateYmd) {
      return true
    }

    return false
  }
}

const combineDisabledMatchers = (
  customMatcher: DisabledMatcher | undefined,
  rangeMatcher: Matcher | undefined
): DisabledMatcher | undefined => {
  if (!customMatcher) {
    return rangeMatcher
  }

  if (!rangeMatcher) {
    return customMatcher
  }

  const customMatchers = Array.isArray(customMatcher) ? customMatcher : [customMatcher]

  return [...customMatchers, rangeMatcher]
}

const toCalendarYmd = (date: ConfigType): string => toYmdFromDate(toCalendarDate(date))

type VisibleMonthOptions = {
  dateObj: Date | null
  initialMonth: Date | null
  startMonth: Date | undefined
  endMonth: Date | undefined
}

const resolveVisibleMonth = ({
  dateObj,
  initialMonth,
  startMonth,
  endMonth,
}: VisibleMonthOptions): Date => {
  if (dateObj) {
    return dateObj
  }

  if (initialMonth) {
    if (startMonth && dayjs(initialMonth).isBefore(dayjs(startMonth), 'month')) {
      return startMonth
    }

    if (endMonth && dayjs(initialMonth).isAfter(dayjs(endMonth), 'month')) {
      return endMonth
    }

    return initialMonth
  }

  const currentMonth = toCalendarDate(dayjs().startOf('month'))

  if (startMonth && dayjs(currentMonth).isBefore(dayjs(startMonth), 'month')) {
    return startMonth
  }

  if (endMonth && dayjs(currentMonth).isAfter(dayjs(endMonth), 'month')) {
    return endMonth
  }

  return currentMonth
}

type DatePickerControlOwnProps = {
  label?: ReactNode
  value?: ConfigType
  onChange?: (value: string) => void
  minDate?: ConfigType
  maxDate?: ConfigType
  initialMonth?: ConfigType
  disabled?: DisabledMatcher
  format?: string
  placeholder?: ReactNode
  className?: string
  buttonProps?: FieldTriggerProps
  popoverContentProps?: PopoverContentProps
  preventOpenAutoFocus?: boolean
  showFieldInfo?: boolean
}

export type DatePickerControlProps = DatePickerControlOwnProps &
  Omit<
    CalendarProps,
    keyof DatePickerControlOwnProps | 'mode' | 'selected' | 'onSelect' | 'required'
  >

export function DatePickerControl({
  label,
  value,
  onChange,
  minDate = null,
  maxDate = null,
  initialMonth = null,
  disabled: disabledMatcher = undefined,
  format = 'ddd, MMM D',
  placeholder = 'Pick a date',
  className = '',
  buttonProps = {},
  popoverContentProps = {},
  preventOpenAutoFocus = false,
  showFieldInfo = false,
  ...props
}: DatePickerControlProps) {
  const [open, setOpen] = useState(false)
  const { isReadOnly } = useIsReadOnly()

  const dayjsObj = value ? dayjs(value) : null
  const dateObj = dayjsObj?.isValid() ? toCalendarDate(value) : null
  const dateString = dayjsObj?.isValid() ? dayjsObj.format(format) : ''
  const initialMonthObj = initialMonth ? dayjs(initialMonth) : null
  const minDateObj = minDate ? dayjs(minDate) : null
  const maxDateObj = maxDate ? dayjs(maxDate) : null

  const minDateYmd = minDateObj?.isValid() ? toCalendarYmd(minDateObj) : null
  const maxDateYmd = maxDateObj?.isValid() ? toCalendarYmd(maxDateObj) : null
  const startMonth = minDateObj?.isValid() ? toCalendarDate(minDateObj.startOf('month')) : undefined
  const endMonth = maxDateObj?.isValid() ? toCalendarDate(maxDateObj.endOf('month')) : undefined
  const rangeDisabledMatcher = buildRangeDisabledMatcher(minDateYmd, maxDateYmd)
  const resolvedInitialMonth = initialMonthObj?.isValid()
    ? toCalendarDate(initialMonthObj.startOf('month'))
    : null

  // DayPicker accepts one matcher or many; combine caller-disabled rules with min/max bounds.
  // Basically, there are a variety of formats that react-day-picker accepts to disabled dates or ranges.
  // This should be able to handle them all.
  const combinedDisabledMatcher = combineDisabledMatchers(disabledMatcher, rangeDisabledMatcher)

  // Fallback to showing the current month if no date is selected
  const [month, setMonth] = useState(() =>
    resolveVisibleMonth({
      dateObj,
      initialMonth: resolvedInitialMonth,
      startMonth,
      endMonth,
    })
  )

  const handleOpenChange = (isOpen: boolean) => {
    // Reset the month when the date picker is closed
    if (!isOpen) {
      setMonth(
        resolveVisibleMonth({
          dateObj,
          initialMonth: resolvedInitialMonth,
          startMonth,
          endMonth,
        })
      )
    }
    setOpen(isOpen)
  }

  const handleSelect = (date: Date | undefined) => {
    if (!date) return
    // Parsing with `dayjs` to format to a YYYY-MM-DD date risks off-by-one-day
    // timezone issues. Since we only need a string to pass to `onChange`
    // we'll just get it directly from the date object.
    onChange?.(toYmdFromDate(date))
    setOpen(false)
  }

  return (
    <div className="flex flex-col gap-y-2">
      {!!label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <FieldTrigger
            id="date"
            size="sm"
            placeholder={!value}
            className={cn('w-48 justify-between', className)}
            {...buttonProps}
            disabled={buttonProps.disabled || isReadOnly}
          >
            {dateString ||
              (placeholder ? (
                <Typography
                  variant="supporting"
                  tone={buttonProps.disabled || isReadOnly ? 'muted' : 'inherit'}
                >
                  {placeholder}
                </Typography>
              ) : null)}
            <ChevronDownIcon className="ml-auto" />
            <span className="sr-only">Select date</span>
          </FieldTrigger>
        </PopoverTrigger>
        <PopoverContent
          size="auto"
          padding="none"
          overflow="hidden"
          align="start"
          onOpenAutoFocus={preventOpenAutoFocus ? (event) => event.preventDefault() : undefined}
          {...popoverContentProps}
        >
          <Calendar
            mode="single"
            selected={dateObj ?? undefined}
            captionLayout="dropdown-years"
            showOutsideDays={false}
            startMonth={startMonth}
            endMonth={endMonth}
            month={month}
            onMonthChange={setMonth}
            onSelect={handleSelect}
            disabled={combinedDisabledMatcher}
            {...props}
          />
        </PopoverContent>
      </Popover>
      {showFieldInfo ? <FieldInfo /> : null}
    </div>
  )
}

export type DatePickerProps = Omit<DatePickerControlProps, 'value' | 'onChange' | 'showFieldInfo'>

export function DatePicker(props: DatePickerProps) {
  const field = useFieldContext<string | null>()

  return (
    <DatePickerControl
      {...props}
      value={field.state.value}
      onChange={field.handleChange}
      showFieldInfo
    />
  )
}

export default DatePicker
